import { jdmLinkOf } from './jdm'
import type { PolicyDocView } from './types'
import { definitionFingerprint } from './versions'

/* Test claims come from Nucleus's deterministic, engine-verified path
   fixtures. The worker never authors a claim or runs the engine itself — it
   only stores what Nucleus returned and what the reviewer judged about it.
   Claims never regenerate on their own: a stale set stays visible until
   someone asks for a fresh one. */

export type ClaimOutcome =
  | 'verified'
  | 'mismatch'
  | 'unverified'
  | 'engine_error'
  | 'unsolvable'

export interface ClaimIndexEntry {
  name: string
  pathKey: string
  leafLabel: string
  outcome: ClaimOutcome
}

export interface ClaimSetSummary {
  nucleusVersion: string
  graphSha: string
  generatedAt: string
  generatedBy: string
  pathsTotal: number
  pathsTruncated: number
  cases: ClaimIndexEntry[]
}

export interface ClaimJudgment {
  verdict: 'agree' | 'disagree'
  cause: 'unclear-intent' | 'bad-claim' | 'wrong-logic' | null
  note: string | null
  by: string
  at: string
  graphSha: string
}

export interface UnreachableMark {
  reason: string
  by: string
  at: string
  graphSha: string
}

export type PathStatus =
  | 'covered'
  | 'unreachable'
  | 'not-generated'
  | 'mismatched'

/** The full Nucleus `POST /api/test-claims/generate` response, structurally. */
export interface NucleusClaimResponse {
  version: string
  fixture: {
    graph_sha256: string
    status: string
    coverage: {
      paths_total: number
      paths_truncated: number
      cases_total: number
      verified: number
      mismatched: number
      unverified: number
      engine_errors: number
      unsolvable: {
        case: string
        node: string
        want: boolean
        reason: string
        detail?: string
      }[]
    }
    cases: {
      name: string
      path: {
        key: string
        steps: { node: string; statement: string; want: boolean }[]
        leaf: string
        expected_label: string
        expected_insight_type: string
      }
      context: Record<string, unknown> | null
      outcome: ClaimOutcome
      actual: {
        statements: Record<string, string>
        label: string | null
      } | null
      reason: string | null
    }[]
  }
}

/** Maps a stored Nucleus fixture into the room's claim-set summary. */
export const toClaimSetSummary = (
  fixture: NucleusClaimResponse['fixture'],
  meta: { nucleusVersion: string; generatedAt: string; generatedBy: string },
): ClaimSetSummary => ({
  nucleusVersion: meta.nucleusVersion,
  graphSha: fixture.graph_sha256,
  generatedAt: meta.generatedAt,
  generatedBy: meta.generatedBy,
  pathsTotal: fixture.coverage.paths_total,
  pathsTruncated: fixture.coverage.paths_truncated,
  cases: fixture.cases.map((entry) => ({
    name: entry.name,
    pathKey: entry.path.key,
    leafLabel: entry.path.expected_label,
    outcome: entry.outcome,
  })),
})

type ClaimsView = Pick<PolicyDocView, 'claims' | 'jdm'>

/** A claim set no longer describes the tree: the tree synced to a different
 *  Nucleus version since, or the sync itself isn't current. */
export const claimsStale = (view: ClaimsView): boolean =>
  !view.claims ||
  view.claims.nucleusVersion !== view.jdm.version ||
  view.jdm.status !== 'synced'

/** A judgment or mark counts only when it was made against the claim set's current graph. */
const isCurrent = (
  entry: { graphSha: string } | undefined,
  graphSha: string,
): boolean => entry !== undefined && entry.graphSha === graphSha

/** A case counts as excluded from coverage when it was judged `disagree` with cause `bad-claim`. */
const isExcluded = (
  judgments: Record<string, ClaimJudgment>,
  graphSha: string,
  name: string,
): boolean => {
  const judgment = judgments[name]
  return (
    isCurrent(judgment, graphSha) &&
    judgment!.verdict === 'disagree' &&
    judgment!.cause === 'bad-claim'
  )
}

type PathsView = Pick<PolicyDocView, 'claims' | 'judgments' | 'unreachable'>

/**
 * The status of every path: the paths with generated cases, plus any path
 * marked unreachable even though it generated none.
 */
export const pathStatuses = (
  view: PathsView,
): { pathKey: string; status: PathStatus }[] => {
  const { claims } = view
  if (!claims) return []
  const pathKeys = new Set([
    ...claims.cases.map((entry) => entry.pathKey),
    ...Object.entries(view.unreachable)
      .filter(([, mark]) => isCurrent(mark, claims.graphSha))
      .map(([pathKey]) => pathKey),
  ])
  return [...pathKeys].map((pathKey) => {
    if (isCurrent(view.unreachable[pathKey], claims.graphSha))
      return { pathKey, status: 'unreachable' as const }
    const cases = claims.cases.filter((entry) => entry.pathKey === pathKey)
    if (
      cases.some(
        (entry) =>
          entry.outcome === 'verified' &&
          !isExcluded(view.judgments, claims.graphSha, entry.name),
      )
    )
      return { pathKey, status: 'covered' as const }
    if (cases.some((entry) => entry.outcome === 'mismatch'))
      return { pathKey, status: 'mismatched' as const }
    return { pathKey, status: 'not-generated' as const }
  })
}

/** Path-status counts. `total` is every unique path plus the paths Nucleus
 *  truncated, which are themselves counted as not generated. */
export const coverage = (
  view: PathsView,
): Record<PathStatus, number> & { total: number } => {
  const statuses = pathStatuses(view)
  const counts: Record<PathStatus, number> = {
    covered: 0,
    unreachable: 0,
    'not-generated': 0,
    mismatched: 0,
  }
  for (const { status } of statuses) counts[status] += 1
  const truncated = view.claims?.pathsTruncated ?? 0
  counts['not-generated'] += truncated
  return { ...counts, total: statuses.length + truncated }
}

type BlockerView = PolicyDocView

const agreedVerified = (view: BlockerView, name: string): boolean => {
  const { claims } = view
  if (!claims) return false
  const entry = claims.cases.find((c) => c.name === name)
  const judgment = view.judgments[name]
  return (
    !!entry &&
    entry.outcome === 'verified' &&
    isCurrent(judgment, claims.graphSha) &&
    judgment!.verdict === 'agree'
  )
}

/**
 * B1 (pending Product's D7 decision): a policy NURO didn't draft has no
 * Nucleus claims, so design links external test results instead. The link is
 * a decision pinned to the definition, so any later edit makes it stale.
 */
export const externalClaimsCurrent = (
  view: Parameters<typeof definitionFingerprint>[0] & Pick<PolicyDocView, 'decisions'>,
): boolean => {
  const attested = view.decisions.filter((d) => d.decision === 'claims-attested')
  if (!attested.length) return false
  const latest = attested.reduce((a, b) => (b.seq > a.seq ? b : a))
  return latest.fingerprint === definitionFingerprint(view)
}

/** Why the tree cannot go to validation on its claims, in order: no link, no
 *  claims, stale claims, an empty tree, then every uncovered path and
 *  unclaimed outcome. */
export const claimBlockers = (view: BlockerView): string[] => {
  if (!jdmLinkOf(view.meta))
    return externalClaimsCurrent(view)
      ? []
      : [
          'This policy wasn’t drafted by NURO — link external test results for this version',
        ]
  if (!view.jdm.version) return ['Generate test claims']
  if (!view.claims) return ['Generate test claims']
  if (claimsStale(view)) return ['Test claims are out of date — regenerate']
  const total = coverage(view).total
  if (total === 0) return ['The tree has no paths']
  const { claims } = view
  const pathBlockers = pathStatuses(view)
    .filter((entry) => entry.status !== 'unreachable')
    .filter(
      (entry) =>
        !claims!.cases.some(
          (c) => c.pathKey === entry.pathKey && agreedVerified(view, c.name),
        ),
    )
    .map((entry) => `Path ${entry.pathKey} has no agreed claim`)
  const insightBlockers = view.insights
    .filter(
      (insight) =>
        !claims!.cases.some(
          (c) => c.leafLabel === insight.label && agreedVerified(view, c.name),
        ),
    )
    .map((insight) => `Outcome "${insight.label}" has no agreed claim`)
  return [...pathBlockers, ...insightBlockers]
}

/** Why `judge-claim` would be refused for this verdict, or `[]` when it would not. */
export const judgeBlockers = (
  view: Pick<PolicyDocView, 'claims'>,
  name: string,
  verdict: 'agree' | 'disagree',
  cause: 'unclear-intent' | 'bad-claim' | 'wrong-logic' | undefined,
  note: string | undefined,
  question: string | undefined,
): string[] => {
  const outcome = view.claims?.cases.find((c) => c.name === name)?.outcome
  if (verdict === 'agree' && outcome !== 'verified')
    return ['Only a claim the engine ran can be agreed']
  if (verdict === 'disagree' && !cause)
    return ['Say what’s wrong with this claim']
  if (cause === 'unclear-intent' && !question?.trim())
    return ['Ask the question this claim raises']
  if (cause === 'bad-claim' && !note?.trim())
    return ['Explain why this claim is bad']
  return []
}

/** `cue.*`/`clue.*` field reads in a set of JDM switch conditions, plus
 *  `ocls` when any condition reads the OCL history object. */
export const readsFields = (conditions: string[]): string[] => {
  const fields = new Set<string>()
  for (const condition of conditions) {
    for (const match of condition.matchAll(/\b(cue|clue)\.([A-Za-z_]\w*)/g))
      fields.add(`${match[1]}.${match[2]}`)
    if (/\bocls\b/.test(condition)) fields.add('ocls')
  }
  return [...fields]
}
